import { useEffect, useState } from "react";
import { UserPlus, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { type AssignableProfile } from "@/hooks/useAssignableProfiles";
import { AssignableUserOptions } from "@/components/common/AssignableUserOptions";
import { useAssignInspectionMutation } from "@/hooks/useOpenInspectionsQuery";

/**
 * Radix Select forbids empty-string values, so "remove assignment" uses an
 * explicit sentinel that is mapped back to `null` before the update.
 */
const UNASSIGN_VALUE = "__unassign__";

interface AssignInspectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Inspection being (re)assigned; null while the dialog is closed. */
  inspectionId: string | null;
  /** Human label for the row (Prüfobjekt / Vorlage) shown in the description. */
  inspectionLabel: string;
  /** Currently assigned user id, to preselect. */
  currentAssignee: string | null;
  /** Team-scoped assignable users. */
  assignableUsers: AssignableProfile[];
}

/**
 * AssignInspectionDialog (TES-506) — assigns/reassigns a single open inspection
 * via `inspections.assigned_to`. The mutation runs through the user-JWT-scoped
 * client so RLS gates the row (CLAUDE.md: never service_role).
 */
export function AssignInspectionDialog({
  open,
  onOpenChange,
  inspectionId,
  inspectionLabel,
  currentAssignee,
  assignableUsers,
}: AssignInspectionDialogProps) {
  const { t } = useTranslation();
  const [selectedValue, setSelectedValue] = useState<string>("");
  const mutation = useAssignInspectionMutation();

  // Preselect the current assignee (or the unassign sentinel) each time it opens.
  useEffect(() => {
    if (open) setSelectedValue(currentAssignee ?? UNASSIGN_VALUE);
  }, [open, currentAssignee]);

  const handleSubmit = () => {
    if (!inspectionId) return;
    const assignedTo = selectedValue === UNASSIGN_VALUE ? null : selectedValue || null;
    mutation.mutate(
      { inspectionId, assignedTo },
      {
        onSuccess: () => {
          toast.success(
            assignedTo ? t("openInspections.assignSuccess") : t("openInspections.unassignSuccess"),
          );
          onOpenChange(false);
        },
        onError: () => {
          toast.error(t("common.error"), { description: t("openInspections.assignError") });
        },
      },
    );
  };

  const canProceed = selectedValue !== "" && !!inspectionId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="assign-inspection-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" />
            {t("openInspections.assignTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("openInspections.assignDesc", { name: inspectionLabel })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Select value={selectedValue} onValueChange={setSelectedValue}>
            <SelectTrigger
              data-testid="assign-inspection-select"
              aria-label={t("openInspections.assignTitle")}
            >
              <SelectValue placeholder={t("openInspections.selectUser")} />
            </SelectTrigger>
            <SelectContent>
              <AssignableUserOptions
                unassignValue={UNASSIGN_VALUE}
                unassignLabel={t("openInspections.removeAssignment")}
                users={assignableUsers}
              />
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canProceed || mutation.isPending}
            data-testid="assign-inspection-confirm"
          >
            {mutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t("openInspections.assigning")}
              </>
            ) : (
              <>
                <UserPlus className="h-4 w-4 mr-2" />
                {t("openInspections.assign")}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
